import { Button, TextField, Typography } from "@mui/material";
import { PlateType } from "features/WorklistTools/shared/components/PlateType";
import { SetLabwareType } from "features/WorklistTools/shared/components/SetLabwareType";
import { SourcePlate } from "features/WorklistTools/shared/components/SourcePlate";
import "features/WorklistTools/shared/components/styles/WorklistGenerator.scss";
import {
  UploadedSourcePlate,
  UploadSourcePlateDialog,
} from "features/WorklistTools/shared/components/UploadSourcePlateDialog";
import {
  PlatePosition,
  WorklistTools,
} from "features/WorklistTools/shared/interfaces";
import {
  GetLabwareTypesQuery,
  LookupLabwareType,
  useGetPlatesInfoLazyQuery,
  usePlateHasTubeBarcodeScanLazyQuery,
  useSetLabwareTypeMutation,
} from "graphql/generated/schema-types";
import { useAppReduxDispatch, useAppReduxSelector } from "hooks/reduxHooks";
import React, { useState } from "react";
import { ConfirmDialog } from "shared-components/ModalsAndDialogs/ConfirmDialog";
import { useConfirm } from "state-provider/ConfirmDialogProvider/hooks";
import styled from "styled-components";
import { ExternalThawToolActions, SourcePlateInfoState } from "../state";
import { ExternalThawInternalAction } from "../state/actions";
import { useExternalThawStateProvider } from "../state/StateProvider";
import { handleUploadPlate } from "./handlers/HandleUploadPlate";

const StyledSourcePlateDiv = styled("div")`
  height: 320px;
  width: 100%;
`;

const StyledPlateTypeContainer = styled.div`
  margin-top: 15px;
`;

const StyledPlateActions = styled.div`
  width: 100%;
  margin-top: 7px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 10px;
`;

export const SourcePlatesContainer = (sourcePlateTypeInfo: any) => {
  const { isConfirmed } = useConfirm();
  const [selectedPlateIndex, setSelectedPlateIndex] = useState(0);
  const [uploadPlateDialogOpen, setUploadPlateDialogOpen] = useState(false);
  const [labwareTypeDialogOpen, setLabwareTypeDialogOpen] = useState(false);

  const { externalThawInternalState, externalThawDispatch } =
    useExternalThawStateProvider();
  const dispatch = useAppReduxDispatch();
  const [colorIndex, setColorIndex] = useState(0);
  const [getPlateInfo, { data: plateInfo, refetch }] =
    useGetPlatesInfoLazyQuery({
      onCompleted: (data) => {
        handleUploadPlate({
          data,
          setLabwareTypeDialogOpen,
          uploadIntermediatePlate: false,
          sourcePlateInfo,
          dispatch,
          colorIndex,
          setColorIndex,
          selectedPlateIndex,
        });
      },
    });
  const [setLabwareType] = useSetLabwareTypeMutation();
  const sourcePlateInfo = useAppReduxSelector(
    (state) => state.WorklistTools.ExternalThawTool.present.sourcePlateInfo
  );

  const intPlateInfo = useAppReduxSelector(
    (state) => state.WorklistTools.ExternalThawTool.present.intPlateInfo
  );

  const handleUploadSourcePlateClose = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    plate: UploadedSourcePlate | undefined,
    data: GetLabwareTypesQuery | undefined
  ) => {
    setUploadPlateDialogOpen(false);
    getPlateInfo({
      variables: {
        where: {
          plateCreatedId: { eq: plate!.plateCreatedId },
          containers: {
            some: {
              isActive: { eq: true },
            },
          },
        },
      },
    });
    plateHasBarcodeScan({ variables: { plateId: plate?.plateCreatedId ?? 0 } });
  };

  const handleSetLabwareTypeClose = (
    plateId: number,
    labwareType: LookupLabwareType | undefined,
    saveChanges: boolean
  ) => {
    setLabwareTypeDialogOpen(false);
    if (saveChanges) {
      const labwareTypeId = labwareType!.labwareTypeId;
      setLabwareType({
        variables: {
          plateId,
          labwareTypeId,
        },
      }).then(() => {
        refetch!().then((refetchData) => {
          handleUploadPlate({
            data: refetchData.data,
            setLabwareTypeDialogOpen,
            uploadIntermediatePlate: false,
            sourcePlateInfo,
            dispatch,
            colorIndex,
            setColorIndex,
            selectedPlateIndex,
          });
        });
      });
    }
  };

  const [plateHasBarcodeScan, { data: hasTubeBarcodeScan }] =
    usePlateHasTubeBarcodeScanLazyQuery({
      onCompleted: () => {
        externalThawDispatch({
          type: ExternalThawInternalAction.SET_PLATE_HAS_TUBE_BARCODE_SCAN,
          payload: {
            sourcePlateHasTubeBarcodeScan:
              hasTubeBarcodeScan?.plateHasTubeBarcodeScan,
          },
        });
      },
    });

  const handleUploadPlateClick = (index: number) => {
    setUploadPlateDialogOpen(true);
    setSelectedPlateIndex(index);
  };

  const handleRemovePlateClick = async (index: number) => {
    const confirmed = await isConfirmed(
      "Are you sure you want to remove this plate? NOTE: You can't add the plate back"
    );

    if (!confirmed) {
      return;
    }
    dispatch(
      ExternalThawToolActions.CLEAR_SOURCE_PLATE({
        index,
      })
    );
  };

  const systemOrPrvNotSet = () => {
    return (
      externalThawInternalState.methodSettings!.selectedSystem === 0 ||
      intPlateInfo[0].resuspensionVol < 200 ||
      intPlateInfo[0].resuspensionVol > 2000
    );
  };

  const disableUploadButton = (plateIndex: number) => {
    return (
      sourcePlateInfo[plateIndex].plateBarcode !== "" || systemOrPrvNotSet()
    );
  };

  return (
    <div
      style={{
        height: "calc(100vh - 300px)",
        width: "600px",
        overflow: "auto",
      }}
    >
      {sourcePlateInfo.map((item: SourcePlateInfoState, index: number) => (
        <div style={{ padding: "20px" }}>
          <React.Fragment>
            <TextField
              variant="outlined"
              style={{ width: "100%", marginBottom: "10px" }}
              disabled
              label="Source Plate Barcode"
              value={item.plateBarcode}
              InputProps={{
                startAdornment: <b>{index + 1}| </b>,
              }}
            />
            <React.Fragment>
              <StyledSourcePlateDiv>
                <SourcePlate
                  worklistTool={WorklistTools.ExternalThaw}
                  index={index}
                  wellInfo={item.wellInfo}
                  plateRows={item.rows}
                  plateCols={item.cols}
                />
              </StyledSourcePlateDiv>
              <StyledPlateActions>
                <Button
                  variant="outlined"
                  onClick={() => handleUploadPlateClick(index)}
                  disabled={disableUploadButton(index)}
                >
                  Upload Plate
                </Button>
                <Button
                  variant="outlined"
                  disabled={item.rows.length < 1}
                  onClick={() => handleRemovePlateClick(index)}
                >
                  Remove Plate
                </Button>
                {systemOrPrvNotSet() && (
                  <Typography color="error" variant="body2">
                    {"Select System and set PRV to upload plate"}
                  </Typography>
                )}
              </StyledPlateActions>
              {item.rows.length > 0 && item.cols.length > 0 ? (
                <StyledPlateTypeContainer>
                  <PlateType
                    platePosition={PlatePosition.Source}
                    isDisabled={true}
                    plateType={item.labwareTypeCode}
                    acceptedPlateTypes={sourcePlateTypeInfo.sourcePlateInfo}
                  />
                </StyledPlateTypeContainer>
              ) : null}
            </React.Fragment>
          </React.Fragment>
        </div>
      ))}

      <UploadSourcePlateDialog
        isOpen={uploadPlateDialogOpen}
        handleClose={() => setUploadPlateDialogOpen(false)}
        handleUpload={handleUploadSourcePlateClose}
        queryVariables={
          sourcePlateTypeInfo.sourcePlateInfo
            ? {
                where: {
                  labwareTypeId: {
                    in: sourcePlateTypeInfo.sourcePlateInfo.map((e: any) => {
                      return e.labwareTypeId;
                    }),
                  },
                  arrayPlateRoleId: {
                    eq: 3,
                  },
                  plateBarcode: {
                    nin: sourcePlateInfo.map((item) => item.plateBarcode),
                  },
                },
              }
            : {}
        }
        title="Select a Source Plate to Upload"
      ></UploadSourcePlateDialog>

      <SetLabwareType
        isOpen={labwareTypeDialogOpen}
        handleClose={(plateId, labwareType, saveChanges) =>
          handleSetLabwareTypeClose(plateId, labwareType, saveChanges)
        }
        plateId={plateInfo?.platesInfo?.plateCreatedId ?? 0}
        plateBarcode={plateInfo?.platesInfo?.plateBarcode ?? ""}
        plateLayoutTypeId={
          plateInfo?.platesInfo?.arrayPlateType?.plateLayoutTypeId ?? 0
        }
      ></SetLabwareType>

      <ConfirmDialog />
    </div>
  );
};
